import { z } from "zod";

// ===== Payroll: staff salaries + salary payments =====
// The staff side of the finance system. staff_salaries holds the AGREED monthly
// salary per member; salary_payments is the ledger of actual disbursements. Every
// payment also writes a clinic_expenses row (category 'salaries') atomically, so
// payroll flows straight into the same P&L as every other expense — the owner
// sees one consistent profit/loss, never a payroll figure that disagrees with it.

/** 'YYYY-MM' — the month a salary payment is FOR. */
export const periodMonthSchema = z
	.string()
	.regex(/^\d{4}-(0[1-9]|1[0-2])$/, "period must be YYYY-MM");

export const salaryLastPaymentSchema = z.object({
	period_month: z.string(),
	amount: z.number(),
	paid_at: z.string(),
});
export type SalaryLastPayment = z.infer<typeof salaryLastPaymentSchema>;

export const staffSalaryApiSchema = z.object({
	id: z.string().uuid().nullable(),
	clinic_id: z.string().uuid(),
	member_id: z.string().uuid(),
	/** Display name of the member (from users.fullName), title if present. */
	member_name: z.string(),
	member_title: z.string().nullable(),
	role: z.string(),
	/** null when no salary has been set for this member yet. */
	monthly_amount: z.number().nullable(),
	currency: z.string(),
	is_active: z.boolean(),
	notes: z.string().nullable(),
	/** Most recent disbursement, if any — lets the UI show "last paid". */
	last_payment: salaryLastPaymentSchema.nullable(),
});
export type StaffSalaryApi = z.infer<typeof staffSalaryApiSchema>;

export const setSalaryInputSchema = z.object({
	member_id: z.string().uuid(),
	monthly_amount: z.number().positive().max(1_000_000_000),
	currency: z.string().min(1).max(8).optional(),
	is_active: z.boolean().optional(),
	notes: z.string().max(500).nullable().optional(),
});
export type SetSalaryInput = z.infer<typeof setSalaryInputSchema>;

export const salaryPaymentApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	member_id: z.string().uuid().nullable(),
	member_name_snapshot: z.string(),
	amount: z.number(),
	currency: z.string(),
	period_month: z.string(),
	expense_id: z.string().uuid().nullable(),
	paid_at: z.string(),
	notes: z.string().nullable(),
});
export type SalaryPaymentApi = z.infer<typeof salaryPaymentApiSchema>;

export const paySalaryInputSchema = z.object({
	member_id: z.string().uuid(),
	/** Defaults to the member's agreed monthly_amount when omitted server-side. */
	amount: z.number().positive().max(1_000_000_000).optional(),
	period_month: periodMonthSchema,
	notes: z.string().max(500).nullable().optional(),
});
export type PaySalaryInput = z.infer<typeof paySalaryInputSchema>;

export const listSalaryPaymentsQuerySchema = z.object({
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
	member_id: z.string().uuid().optional(),
});
export type ListSalaryPaymentsQuery = z.infer<
	typeof listSalaryPaymentsQuerySchema
>;
